import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  Divider,
  Code,
  Table,
  Tbody,
  Tr,
  Td,
  TableContainer,
  UnorderedList,
  ListItem,
  Alert,
  AlertIcon,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  ArrowBackIcon,
  CopyIcon,
  CheckCircleIcon,
} from '@chakra-ui/icons';

function Results({ data, onBack }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = data.instructions.instructions;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Format instructions into readable sections
  const formatInstructions = (text) => {
    const lines = text.split('\n');
    const formatted = [];
    let currentSection = null;

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Main step (starts with number) or section header (all caps)
      if (/^\d+\./.test(trimmed) || /^Step \d+/.test(trimmed) || /^[A-Z][A-Z\s]+$/.test(trimmed)) {
        if (currentSection) formatted.push(currentSection);
        currentSection = { type: 'step', title: trimmed, items: [] };
      }
      // Option heading
      else if (trimmed.startsWith('Option ')) {
        if (currentSection) formatted.push(currentSection);
        currentSection = { type: 'option', title: trimmed, items: [] };
      }
      // Sub-item (starts with letter or dash)
      else if (/^[a-z]\)/.test(trimmed) || trimmed.startsWith('-')) {
        if (currentSection) {
          currentSection.items.push(trimmed);
        } else {
          formatted.push({ type: 'text', content: trimmed });
        }
      }
      // Regular text
      else {
        if (currentSection) {
          currentSection.items.push(trimmed);
        } else {
          formatted.push({ type: 'text', content: trimmed });
        }
      }
    });

    if (currentSection) formatted.push(currentSection);
    return formatted;
  };

  const formattedInstructions = formatInstructions(data.instructions.instructions);

  return (
    <VStack spacing={6} align="stretch">
      {/* Back Button */}
      <Button
        leftIcon={<ArrowBackIcon />}
        onClick={onBack}
        variant="ghost"
        colorScheme="purple"
        alignSelf="flex-start"
        size="sm"
        color="gray.400"
        _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
      >
        Back to Search
      </Button>

      {/* Domain Information Card */}
      <Box
        bg="#1A1F2E"
        p={8}
        borderRadius="lg"
        boxShadow="lg"
        borderWidth="1px"
        borderColor="gray.700"
      >
        <VStack align="stretch" spacing={6}>
          {/* Domain Header */}
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Heading size="lg" fontFamily="mono" color="white" letterSpacing="tight">
              {data.domain}
            </Heading>
            <Badge
              colorScheme={data.status === 'Active' ? 'green' : 'red'}
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="md"
              textTransform="uppercase"
              fontWeight="bold"
            >
              {data.status}
            </Badge>
          </Flex>

          <Divider borderColor="gray.700" />

          {/* Domain Details Table */}
          <TableContainer>
            <Table variant="simple" size="md">
              <Tbody>
                <Tr>
                  <Td borderColor="gray.700" color="gray.400" fontWeight="semibold" w="200px" fontSize="sm">
                    Registrar
                  </Td>
                  <Td borderColor="gray.700" color="white" fontSize="md">
                    {data.registrar}
                  </Td>
                </Tr>
                <Tr>
                  <Td borderColor="gray.700" color="gray.400" fontWeight="semibold" fontSize="sm">
                    DNS Provider
                  </Td>
                  <Td borderColor="gray.700">
                    <HStack>
                      <Code colorScheme="purple" fontSize="sm" px={2} py={1}>
                        {data.dnsProvider}
                      </Code>
                      {data.isRegistrarDNS && (
                        <Badge colorScheme="blue" fontSize="xs">
                          Same as registrar
                        </Badge>
                      )}
                    </HStack>
                  </Td>
                </Tr>
                {data.expirationDate && (
                  <Tr>
                    <Td borderColor="gray.700" color="gray.400" fontWeight="semibold" fontSize="sm">
                      Expiration Date
                    </Td>
                    <Td borderColor="gray.700" color="white">
                      {data.expirationDate}
                    </Td>
                  </Tr>
                )}
                <Tr>
                  <Td borderColor="gray.700" color="gray.400" fontWeight="semibold" fontSize="sm" verticalAlign="top">
                    Nameservers
                  </Td>
                  <Td borderColor="gray.700">
                    <VStack align="start" spacing={2}>
                      {data.nameservers && data.nameservers.length > 0 ? (
                        data.nameservers.map((ns, index) => (
                          <Code
                            key={index}
                            fontSize="xs"
                            bg="gray.900"
                            px={2}
                            py={1}
                            borderRadius="sm"
                            fontFamily="mono"
                            color="gray.300"
                          >
                            {ns}
                          </Code>
                        ))
                      ) : (
                        <Text color="gray.500" fontSize="sm">No nameservers found</Text>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </VStack>
      </Box>

      {/* Instructions Card */}
      <Box
        bg="#1A1F2E"
        p={8}
        borderRadius="lg"
        boxShadow="lg"
        borderWidth="1px"
        borderColor="gray.700"
      >
        <VStack align="stretch" spacing={6}>
          {/* Instructions Header */}
          <HStack justify="space-between" align="center">
            <Heading size="md" color="white">
              DNS Access Instructions
            </Heading>
            <Button
              leftIcon={copied ? <CheckCircleIcon /> : <CopyIcon />}
              onClick={handleCopy}
              size="sm"
              colorScheme={copied ? 'green' : 'purple'}
              variant={copied ? 'solid' : 'outline'}
            >
              {copied ? 'Copied!' : 'Copy All'}
            </Button>
          </HStack>

          {/* Scenario Badge */}
          <Badge
            alignSelf="flex-start"
            colorScheme="pink"
            fontSize="xs"
            px={3}
            py={1}
            borderRadius="md"
            textTransform="uppercase"
          >
            {data.instructions.scenario}
          </Badge>

          <Divider borderColor="gray.700" />

          {/* Formatted Instructions */}
          <Box
            bg="#0F1419"
            p={6}
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.700"
          >
            <VStack align="stretch" spacing={5}>
              {(() => {
                // Group consecutive options together for tabs
                const grouped = [];
                let currentOptions = [];

                formattedInstructions.forEach((section, index) => {
                  if (section.type === 'option') {
                    currentOptions.push(section);
                  } else {
                    if (currentOptions.length > 0) {
                      grouped.push({ type: 'options', options: currentOptions });
                      currentOptions = [];
                    }
                    grouped.push(section);
                  }
                });

                if (currentOptions.length > 0) {
                  grouped.push({ type: 'options', options: currentOptions });
                }

                return grouped.map((item, index) => (
                  <Box key={index}>
                    {item.type === 'step' && (
                      <Box>
                        <Heading size="sm" color="white" mb={3}>
                          {item.title}
                        </Heading>
                        {item.items.length > 0 && (
                          <UnorderedList spacing={2} pl={4} mt={2}>
                            {item.items.map((subItem, i) => (
                              <ListItem key={i} color="gray.300" fontSize="sm" lineHeight="tall">
                                {subItem}
                              </ListItem>
                            ))}
                          </UnorderedList>
                        )}
                      </Box>
                    )}

                    {item.type === 'options' && (
                      <Tabs
                        variant="enclosed"
                        colorScheme="purple"
                        size="md"
                      >
                        <TabList borderColor="gray.600">
                          {item.options.map((option, i) => (
                            <Tab
                              key={i}
                              color="gray.400"
                              _selected={{
                                color: 'white',
                                bg: '#1A1F2E',
                                borderColor: 'gray.600',
                                borderBottomColor: '#1A1F2E',
                              }}
                              fontWeight="semibold"
                              fontSize="sm"
                            >
                              {option.title.split(':')[0]}
                            </Tab>
                          ))}
                        </TabList>

                        <TabPanels>
                          {item.options.map((option, i) => (
                            <TabPanel key={i} px={0} py={4}>
                              <VStack align="stretch" spacing={3}>
                                <Text color="gray.400" fontSize="sm" fontStyle="italic">
                                  {option.title.split(':')[1]?.trim()}
                                </Text>
                                {option.items.length > 0 && (
                                  <UnorderedList spacing={2} pl={4}>
                                    {option.items.map((subItem, j) => (
                                      <ListItem key={j} color="gray.300" fontSize="sm" lineHeight="tall">
                                        {subItem}
                                      </ListItem>
                                    ))}
                                  </UnorderedList>
                                )}
                              </VStack>
                            </TabPanel>
                          ))}
                        </TabPanels>
                      </Tabs>
                    )}

                    {item.type === 'text' && (
                      <Text color="gray.300" fontSize="sm" lineHeight="tall">
                        {item.content}
                      </Text>
                    )}
                  </Box>
                ));
              })()}
            </VStack>
          </Box>

          {/* Important Notes */}
          {data.instructions.notes && data.instructions.notes.length > 0 && (
            <Alert
              status="info"
              borderRadius="md"
              bg="blue.900"
              borderColor="blue.700"
              borderWidth="1px"
            >
              <AlertIcon color="blue.300" />
              <Box>
                <Text fontWeight="bold" mb={2} color="white" fontSize="sm">
                  Important Notes
                </Text>
                <VStack align="start" spacing={1}>
                  {data.instructions.notes.map((note, index) => (
                    <Text key={index} fontSize="xs" color="gray.300" lineHeight="tall">
                      • {note}
                    </Text>
                  ))}
                </VStack>
              </Box>
            </Alert>
          )}
        </VStack>
      </Box>
    </VStack>
  );
}

export default Results;
